import { useRef, useEffect, useState } from "react";
import { X, Camera, AlertCircle, Loader2, Database, ImageIcon, Upload } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { supabase } from "@/integrations/supabase/client";
import { resolveScannedDryingTechnology, type DryingTechnology } from "@/lib/dryerTechnology";
import { mapEprelToClassification, type ApplianceClassification } from "@/lib/eprelClassification";

const energyLabelPatterns = [
  /energy[_\-]?label/i, /energylabel/i, /energy[_\-]?rating/i, /eek/i,
  /eu[_\-]?label/i, /eulabel/i, /eprel/i, /fiche/i, /datasheet/i,
  /label[_\-]?image/i, /rating[_\-]?label/i, /energy[_\-]?class/i,
  /media\.miele\.com\/downloads\//i,
];
const isEnergyLabelUrl = (url: string): boolean => energyLabelPatterns.some(p => p.test(url));

export interface EprelData {
  energyClass?: string;
  ratedCapacity?: number;
  waterCons?: number;
  energyConsPer100Cycle?: number;
  programmeDurationRated?: number;
  noise?: number;
  noiseClass?: string;
  dimensionHeight?: number;
  dimensionWidth?: number;
  dimensionDepth?: number;
  guaranteeDuration?: number;
  onMarketStartDate?: number[];
  eprelRegistrationNumber?: string;
  supplierOrTrademark?: string;
  modelIdentifier?: string;
}

export interface OCRResult {
  modelNumber?: string | null;
  serialNumber?: string | null;
  productNumber?: string | null;
  type?: string | null;
  /** "Type" designation printed on the type shield (Miele, BSH, LG, AEG, Sharp, Inventum). */
  plateType?: string | null;
  /** BSH-only: FD number and Z-Nr. printed on Bosch/Siemens plates. */
  fdCode?: string | null;
  zNr?: string | null;
  /** Samsung-only: "Version" printed on the plate. */
  plateVersion?: string | null;
  productionNumber?: string | null;
  brand?: string | null;
  productType?: string | null;
  specifications?: Record<string, string> | null;
  rawText?: string;
  refrigerant?: string | null;
  refrigerantCharge?: string | null;
  washCapacityKg?: number | null;
  dryCapacityKg?: number | null;
  dryerTechnology?: DryingTechnology | null;
  dryerTechnologySource?: "type_shield" | "eprel" | "rule" | "none";
  refrigerantReading?: { code: string; chargeGrams: number | null; evidence: string } | null;
  technologyConflict?: string | null;
  eprelClassification?: ApplianceClassification;
  // EPREL data fetched after OCR
  eprelData?: EprelData | null;
  eprelReference?: string | null;
  // Product image
  productImageUrl?: string | null;
}

interface CameraScannerProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onCapture?: (imageData: string, ocrResult?: OCRResult) => void;
}

export function CameraScanner({ open, onOpenChange, onCapture }: CameraScannerProps) {
  const videoRef = useRef<HTMLVideoElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [stream, setStream] = useState<MediaStream | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [hasPermission, setHasPermission] = useState<boolean | null>(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [cameraReady, setCameraReady] = useState(false);

  // Only handle cleanup when dialog closes
  useEffect(() => {
    if (!open) {
      stopCamera();
      setCameraReady(false);
      setHasPermission(null);
    }
    return () => {
      stopCamera();
    };
  }, [open]);

  // Auto-start the camera as soon as the dialog opens (the click that opened
  // it is the user gesture that authorizes getUserMedia).
  useEffect(() => {
    if (open && !stream && hasPermission !== false && !error) {
      requestCameraAccess();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // Attach stream to video element when both are available
  useEffect(() => {
    if (stream && videoRef.current && cameraReady) {
      if (videoRef.current.srcObject !== stream) {
        videoRef.current.srcObject = stream;
        videoRef.current.play().catch(console.error);
      }
    }
  }, [stream, cameraReady]);

  const requestCameraAccess = async () => {
    setError(null);

    // Basic environment checks — surface clearer diagnostics than "denied"
    if (!window.isSecureContext) {
      setHasPermission(false);
      setError("Camera requires a secure (HTTPS) connection. Open the app over HTTPS and try again.");
      return;
    }
    if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
      setHasPermission(false);
      setError("This browser does not expose camera access. Try the latest Chrome, Safari or Firefox.");
      return;
    }

    try {
      // Do not preflight with navigator.permissions here: some embedded/app
      // contexts report "denied" even when getUserMedia itself is allowed.
      // Let the browser's media API be the source of truth.
      const mediaStream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: "environment" },
        audio: false,
      });

      setStream(mediaStream);
      setHasPermission(true);

      // Wait a tick for React to render the video element
      setTimeout(() => {
        if (videoRef.current) {
          videoRef.current.srcObject = mediaStream;
          videoRef.current.play().then(() => {
            setCameraReady(true);
          }).catch((playErr) => {
            console.error('Video play error:', playErr);
            setCameraReady(true); // Still show camera UI even if autoplay fails
          });
        } else {
          setCameraReady(true);
        }
      }, 100);
    } catch (err) {
      setHasPermission(false);
      setCameraReady(false);
      console.error("getUserMedia error:", err);
      if (err instanceof Error) {
        const name = err.name;
        const msg = err.message || "";
        if (name === "NotAllowedError" || name === "SecurityError") {
          setError("Live camera preview could not be started here. Use Take Photo to continue scanning, or tap Try Again if you want to retry the live camera.");
        } else if (name === "NotFoundError" || name === "OverconstrainedError") {
          setError("No camera was found on this device.");
        } else if (name === "NotReadableError") {
          setError("The camera is already in use by another app or tab. Close it and try again.");
        } else {
          setError(`Unable to access camera: ${msg || name}`);
        }
      }
    }
  };

  const stopCamera = () => {
    if (stream) {
      stream.getTracks().forEach((track) => track.stop());
      setStream(null);
    }
  };

  const processOCR = async (imageData: string): Promise<OCRResult | undefined> => {
    try {
      // Ensure we have a valid user session — refresh if needed — so the
      // edge function receives the user's JWT (not the anon key).
      let { data: sessionData } = await supabase.auth.getSession();
      if (!sessionData.session) {
        const { data: refreshed } = await supabase.auth.refreshSession();
        sessionData = { session: refreshed.session } as typeof sessionData;
      }
      const accessToken = sessionData.session?.access_token;
      if (!accessToken) {
        throw new Error('Your session has expired. Please sign in again to scan.');
      }

      const { data, error } = await supabase.functions.invoke('ocr-scan', {
        body: { imageData },
        headers: { Authorization: `Bearer ${accessToken}` },
      });

      if (error) {
        // supabase-js stuffs the response body into error.context for non-2xx responses.
        // Try to extract the friendly server-provided message (e.g. "AI credits exhausted...").
        let friendly: string | null = null;
        try {
          const ctx = (error as { context?: Response }).context;
          if (ctx && typeof ctx.json === 'function') {
            const body = await ctx.clone().json();
            if (body?.error) friendly = String(body.error);
          }
        } catch { /* ignore parse errors */ }

        if (!friendly && /402/.test(error.message || '')) {
          friendly = 'AI credits exhausted. Please add credits to continue scanning.';
        } else if (!friendly && /429/.test(error.message || '')) {
          friendly = 'Too many scans. Please wait a moment and try again.';
        }

        throw new Error(friendly || error.message || 'OCR processing failed');
      }

      if (!data?.success) {
        throw new Error(data?.error || 'OCR processing failed');
      }

      return data.data as OCRResult;
    } catch (err) {
      console.error('OCR processing error:', err);
      throw err;
    }
  };

  const fetchEprelData = async (ocrResult: OCRResult): Promise<OCRResult> => {
    // Skip if no model number or brand
    if (!ocrResult.modelNumber?.trim() && !ocrResult.brand?.trim()) {
      return ocrResult;
    }

    try {
      setProcessingStage('eprel');
      
      // Determine product group from product type
      const productType = ocrResult.productType?.toLowerCase() || '';
      let productGroup = 'washingmachines2019';
      if (productType.includes('dishwash')) productGroup = 'dishwashers2019';
      else if (productType.includes('fridge') || productType.includes('refriger')) productGroup = 'refrigeratingappliances2019';
      else if (/washer[\s_-]?dryer/.test(productType)) productGroup = 'washerdriers2019';
      else if (productType.includes('dryer')) productGroup = 'tumbledryers2019';

      const withEprel = (payload: { data: Record<string, unknown>; productGroup?: string; classification?: ApplianceClassification }): OCRResult => {
        const classification = payload.classification ?? mapEprelToClassification(payload.data, payload.productGroup || productGroup);
        const dryerTechnology = resolveScannedDryingTechnology(ocrResult.dryerTechnology, classification.dryerTechnology);
        return {
          ...ocrResult,
          eprelData: payload.data as EprelData,
          eprelReference: payload.data.eprelRegistrationNumber as string | undefined,
          eprelClassification: classification,
          dryerTechnology,
          dryerTechnologySource: ocrResult.dryerTechnology ? ocrResult.dryerTechnologySource : dryerTechnology ? "eprel" : "none",
          technologyConflict: ocrResult.dryerTechnology && classification.dryerTechnology && ocrResult.dryerTechnology !== classification.dryerTechnology
            ? `type shield says ${ocrResult.dryerTechnology}, EPREL says ${classification.dryerTechnology}`
            : ocrResult.technologyConflict,
        };
      };

      // Search EPREL by model number (not brand - brand alone won't match)
      const searchTerm = ocrResult.modelNumber || ocrResult.brand || '';
      
      const { data, error } = await supabase.functions.invoke('eprel-search', {
        body: {
          modelNumber: searchTerm,
          productGroup,
        },
      });

      if (error) {
        console.error('EPREL search error:', error);
        return ocrResult;
      }

      if (data?.success && data?.data) {
        // Check if results match our model
        const eprelData = data.data;
        const modelMatch = ocrResult.modelNumber?.toLowerCase().replace(/[\s-]/g, '') || '';
        const eprelModel = (eprelData.modelIdentifier || '').toLowerCase().replace(/[\s-]/g, '');
        
        // If we have search results, try to find exact match
        if (data.searchResults && Array.isArray(data.searchResults)) {
          const exactMatch = data.searchResults.find((result: { modelIdentifier?: string; eprelRegistrationNumber?: string }) => {
            const resultModel = (result.modelIdentifier || '').toLowerCase().replace(/[\s-]/g, '');
            return resultModel.includes(modelMatch) || modelMatch.includes(resultModel.split(' ')[0]);
          });
          
          if (exactMatch) {
            // Fetch full details for the exact match
            const { data: detailData } = await supabase.functions.invoke('eprel-search', {
              body: {
                eprelReference: exactMatch.eprelRegistrationNumber,
                productGroup,
              },
            });
            
            if (detailData?.success && detailData?.data) {
              return withEprel(detailData);
            }
          }
        }
        
        // Check if direct result matches
        if (eprelModel.includes(modelMatch) || modelMatch.includes(eprelModel.split(' ')[0])) {
          return withEprel(data);
        }
      }

      return ocrResult;
    } catch (err) {
      console.error('EPREL fetch error:', err);
      return ocrResult;
    }
  };

  const [processingStage, setProcessingStage] = useState<'ocr' | 'eprel' | 'image' | 'upload' | null>(null);

  const fetchProductImage = async (ocrResult: OCRResult): Promise<OCRResult> => {
    if (!ocrResult.modelNumber && !ocrResult.brand) {
      return ocrResult;
    }

    try {
      setProcessingStage('image');
      
      const { data, error } = await supabase.functions.invoke('product-image-search', {
        body: {
          modelNumber: ocrResult.modelNumber,
          brand: ocrResult.brand,
          productType: ocrResult.productType,
        },
      });

      if (error) {
        console.error('Product image search error:', error);
        return ocrResult;
      }

      const imageUrl = data?.data?.imageUrl || data?.data?.url;
      if (data?.success && imageUrl && !isEnergyLabelUrl(imageUrl)) {
        return {
          ...ocrResult,
          productImageUrl: imageUrl,
        };
      }

      return ocrResult;
    } catch (err) {
      console.error('Product image fetch error:', err);
      return ocrResult;
    }
  };

  const uploadTypeShieldImage = async (imageData: string): Promise<string | null> => {
    try {
      setProcessingStage('upload');
      
      const { data, error } = await supabase.functions.invoke('upload-type-shield', {
        body: { imageData },
      });

      if (error) {
        console.error('Type shield upload error:', error);
        return null;
      }

      if (data?.success && data?.data?.url) {
        console.log('Type shield uploaded:', data.data.url);
        return data.data.url;
      }

      return null;
    } catch (err) {
      console.error('Type shield upload error:', err);
      return null;
    }
  };

  const processCapturedImage = async (imageData: string) => {
    if (!imageData || imageData.length < 100 || !imageData.startsWith('data:image/')) {
      setError('Failed to capture image. Please try again.');
      return;
    }

    setError(null);
    setIsProcessing(true);
    setProcessingStage('ocr');
    try {
      let ocrResult = await processOCR(imageData);
      
      // Auto-fetch EPREL data if we have model info
      if (ocrResult && (ocrResult.modelNumber || ocrResult.brand)) {
        ocrResult = await fetchEprelData(ocrResult);
      }
      
      // Close dialog immediately after OCR + EPREL — don't wait for image search or upload
      stopCamera();
      setIsProcessing(false);
      setProcessingStage(null);
      onCapture?.('', ocrResult);
      onOpenChange(false);
      
      // Run image search and type shield upload in parallel in the background
      // These will update via the useProductImageUpgrade hook on the detail page
      uploadTypeShieldImage(imageData).then(typeShieldUrl => {
        if (typeShieldUrl) {
          // Update the appliance in localStorage with the type shield URL
          console.log('Type shield uploaded in background:', typeShieldUrl);
        }
      }).catch(err => console.error('Background type shield upload failed:', err));
      
      if (ocrResult && (ocrResult.modelNumber || ocrResult.brand)) {
        fetchProductImage(ocrResult).catch(err => 
          console.error('Background product image search failed:', err)
        );
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to process image');
      setIsProcessing(false);
      setProcessingStage(null);
    }
  };

  const handleCapture = async () => {
    if (videoRef.current && canvasRef.current) {
      const video = videoRef.current;
      const canvas = canvasRef.current;
      
      // Ensure video has actual dimensions
      if (video.videoWidth === 0 || video.videoHeight === 0) {
        setError('Camera not ready. Please wait for the video feed to start.');
        return;
      }
      
      canvas.width = video.videoWidth;
      canvas.height = video.videoHeight;
      
      const ctx = canvas.getContext("2d");
      if (ctx) {
        ctx.drawImage(video, 0, 0);
        await processCapturedImage(canvas.toDataURL("image/jpeg", 0.9));
      }
    }
  };

  const handleFileCapture = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (!file) return;

    if (!file.type.startsWith("image/")) {
      setError("Please choose an image of the type shield.");
      return;
    }

    const reader = new FileReader();
    reader.onload = () => {
      if (typeof reader.result === "string") {
        void processCapturedImage(reader.result);
      } else {
        setError("Failed to read the selected image.");
      }
    };
    reader.onerror = () => setError("Failed to read the selected image.");
    reader.readAsDataURL(file);
  };

  const handleClose = () => {
    stopCamera();
    setIsProcessing(false);
    setProcessingStage(null);
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Camera className="w-5 h-5" />
            Scan Type Shield
          </DialogTitle>
        </DialogHeader>
        
        <div className="space-y-4">
          {error ? (
            <div className="flex flex-col items-center gap-4 py-8">
              <div className="w-16 h-16 bg-destructive/10 rounded-full flex items-center justify-center">
                <AlertCircle className="w-8 h-8 text-destructive" />
              </div>
              <p className="text-center text-muted-foreground">{error}</p>
              <div className="flex flex-col sm:flex-row gap-3 w-full">
                <Button onClick={() => fileInputRef.current?.click()} className="flex-1">
                  <Camera className="w-4 h-4 mr-2" />
                  Take Photo
                </Button>
                <Button onClick={() => { setError(null); requestCameraAccess(); }} variant="outline" className="flex-1">
                  Try Again
                </Button>
              </div>
            </div>
          ) : !cameraReady ? (
            <div className="flex flex-col items-center gap-4 py-8">
              <Loader2 className="w-8 h-8 text-primary animate-spin" />
              <p className="text-center text-muted-foreground">Starting camera…</p>
            </div>
          ) : isProcessing ? (
            <div className="flex flex-col items-center gap-4 py-8">
              <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center">
                {processingStage === 'eprel' ? (
                  <Database className="w-8 h-8 text-primary animate-pulse" />
                ) : processingStage === 'image' ? (
                  <ImageIcon className="w-8 h-8 text-primary animate-pulse" />
                ) : processingStage === 'upload' ? (
                  <Upload className="w-8 h-8 text-primary animate-pulse" />
                ) : (
                  <Loader2 className="w-8 h-8 text-primary animate-spin" />
                )}
              </div>
              <p className="text-center text-muted-foreground">
                {processingStage === 'eprel' 
                  ? 'Fetching energy label data from EPREL...'
                  : processingStage === 'image'
                  ? 'Searching for product image...'
                  : processingStage === 'upload'
                  ? 'Saving type shield image...'
                  : 'Processing image with OCR...'}
              </p>
            </div>
          ) : (
            <>
              <div className="relative rounded-lg overflow-hidden bg-black aspect-video">
                <video
                  ref={videoRef}
                  autoPlay
                  playsInline
                  muted
                  className="w-full h-full object-cover"
                />
                <div className="absolute inset-0 pointer-events-none">
                  <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[85%] h-[70%] border-2 border-primary rounded-lg" />
                </div>
              </div>
              
              <p className="text-sm text-center text-muted-foreground">
                Position the type shield label within the frame
              </p>
              
              <div className="flex gap-3">
                <Button variant="outline" onClick={handleClose} className="flex-1">
                  <X className="w-4 h-4 mr-2" />
                  Cancel
                </Button>
                <Button onClick={handleCapture} className="flex-1">
                  <Camera className="w-4 h-4 mr-2" />
                  Capture
                </Button>
              </div>
            </>
          )}
        </div>
        
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          capture="environment"
          className="hidden"
          onChange={handleFileCapture}
        />
        <canvas ref={canvasRef} className="hidden" />
      </DialogContent>
    </Dialog>
  );
}